/**
 * ADMIN KPI NAMING - Registre centralisé
 *
 * Ce fichier centralise le naming, descriptions et métadonnées d'affichage
 * des indicateurs KPI utilisés dans la partie Admin (Pilotage).
 *
 * ⚠️ IMPORTANT :
 * - Le backend reste la source de vérité absolue pour les calculs et payloads KPI
 * - Ce fichier gère UNIQUEMENT :
 *   • label : texte court affiché dans l'UI
 *   • description : description métier visible en UI (ex: tooltip, hint)
 *   • source : chemin technique backend (documentaire, pour traçabilité, JAMAIS affiché)
 *   • format, domain, unit : métadonnées d'affichage
 *
 * - Toute nouvelle KPI Admin DOIT être déclarée ici avant d'être utilisée
 * - NE PAS modifier les clés backend ni les formules métier
 * - NE PAS placer d'informations techniques dans label/description
 */

/** Domaines fonctionnels des KPI */
export enum KpiDomain {
  STAFFING = 'staffing',
  FINANCE = 'finance',
  OPERATIONS = 'operations',
  PROFITABILITY = 'profitability',
}

/** Formats d'affichage des valeurs KPI */
export enum KpiFormat {
  NUMBER = 'number',
  MONEY = 'money',
  PERCENTAGE = 'percentage',
  DECIMAL = 'decimal',
}

/** Unités de mesure */
export enum KpiUnit {
  CONSULTANTS = 'consultants',
  EUROS = '€',
  PERCENT = '%',
  ETP = 'ETP',
  MISSIONS = 'missions',
}

/** Définition d'une entrée KPI */
export interface AdminKpi {
  key: string;
  /** Libellé court affiché dans l'UI (1-5 mots) */
  label: string;
  /** Description claire, lisible pour l'utilisateur métier (UI) */
  description: string;
  /** Source backend technique - documentation de la traçabilité, JAMAIS affichée */
  source: string;
  /** Domaine fonctionnel */
  domain: KpiDomain;
  /** Format d'affichage */
  format: KpiFormat;
  /** Unité de mesure */
  unit: KpiUnit;
}

/**
 * REGISTRE CENTRAL DES KPI ADMIN
 *
 * Contient toutes les KPI utilisées dans la partie Admin/Pilotage.
 * Scope limité aux KPI effectivement affichées.
 */
export const ADMIN_KPI_REGISTRY: Record<string, AdminKpi> = {
  // ===== STAFFING & RESSOURCES =====
  consultants_actifs: {
    key: 'consultants_actifs',
    label: 'Consultants actifs',
    description: 'Nombre de consultants actuellement actifs et disponibles pour missions.',
    source: 'staffing.active_consultants',
    domain: KpiDomain.STAFFING,
    format: KpiFormat.NUMBER,
    unit: KpiUnit.CONSULTANTS,
  },

  taux_occupation: {
    key: 'taux_occupation',
    label: 'Taux d\'occupation (TACE)',
    description: 'TACE (Taux d\'Activité Congés Exclus) : Temps facturé / (Temps total - Congés - Maladie - ABS).',
    source: 'staffing.occupation_pct',
    domain: KpiDomain.STAFFING,
    format: KpiFormat.PERCENTAGE,
    unit: KpiUnit.PERCENT,
  },

  etp_factures_annuels: {
    key: 'etp_factures_annuels',
    label: 'ETP facturés annuels',
    description: 'Équivalents temps plein facturés à client sur l\'année.',
    source: 'comparisons.annual_billed_etp.actual',
    domain: KpiDomain.STAFFING,
    format: KpiFormat.DECIMAL,
    unit: KpiUnit.ETP,
  },

  // ===== FINANCE - KPI DIRECTES =====
  tjm_moyen: {
    key: 'tjm_moyen',
    label: 'TJM net moyen',
    description: 'Taux journalier moyen net facturé aux clients.',
    source: 'comparisons.tjm_net.actual',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  ca_par_consultant: {
    key: 'ca_par_consultant',
    label: 'CA / consultant',
    description: 'Chiffre d\'affaires annuel généré par consultant.',
    source: 'finance.ca_per_consultant_annual',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  ca_mensuel: {
    key: 'ca_mensuel',
    label: 'CA net estimé mensuel',
    description: 'CA net : Chiffre d\'affaires facturable déduit des éventuelles remises commerciales (Mois en cours).',
    source: 'finance.ca_monthly_net',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  ca_annuel: {
    key: 'ca_annuel',
    label: 'CA net annuel',
    description: 'CA net : Chiffre d\'affaires facturable déduit des éventuelles remises commerciales (Année entière).',
    source: 'finance.ca_annual_net',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  ca_annuel_net: {
    key: 'ca_annuel_net',
    label: 'CA annuel net',
    description: 'CA net : Chiffre d\'affaires facturable déduit des éventuelles remises commerciales (Année consolidée).',
    source: 'finance.ca_annual_net',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  charges_fixes_mois: {
    key: 'charges_fixes_mois',
    label: 'Charges fixes (mois)',
    description: 'Dépenses mensuelles structurelles (salaires, loyers, abonnements, etc.).',
    source: 'finance.fixed_costs_monthly_total',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  charges_fixes_annee: {
    key: 'charges_fixes_annee',
    label: 'Charges fixes (année)',
    description: 'Projection annuelle des dépenses structurelles.',
    source: 'finance.fixed_costs_annual_total',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  charges_fixes: {
    key: 'charges_fixes',
    label: 'Charges fixes',
    description: 'Charges de structure.',
    source: 'finance.fixed_costs_monthly_total',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  // ===== PROFITABILITY =====
  benefice_net_mois: {
    key: 'benefice_net_mois',
    label: 'Bénéfice net (mois)',
    description: 'Résultat net mensuel estimé après frais et charges fixes.',
    source: 'finance.net_profit_monthly',
    domain: KpiDomain.PROFITABILITY,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  benefice_net_annee: {
    key: 'benefice_net_annee',
    label: 'Bénéfice net (année)',
    description: 'Résultat net annuel estimé après frais et charges fixes.',
    source: 'finance.net_profit_annual',
    domain: KpiDomain.PROFITABILITY,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  benefice_annuel: {
    key: 'benefice_annuel',
    label: 'Bénéfice annuel',
    description: 'Résultat net consolidé de l\'année.',
    source: 'finance.mission_profit_annual',
    domain: KpiDomain.PROFITABILITY,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  marge_moyenne: {
    key: 'marge_moyenne',
    label: 'Marge nette moyenne',
    description: 'Marge : Différence entre le CA net et le coût total (interne + externe).',
    source: 'finance.average_margin_pct',
    domain: KpiDomain.PROFITABILITY,
    format: KpiFormat.PERCENTAGE,
    unit: KpiUnit.PERCENT,
  },

  perte_intermediaire_mois: {
    key: 'perte_intermediaire_mois',
    label: 'Commissions interm. (mois)',
    description: 'Impact financier mensuel des commissions intermédiaires.',
    source: 'finance.intermediary_loss_monthly',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  perte_intermediaire_annee: {
    key: 'perte_intermediaire_annee',
    label: 'Commissions interm. (année)',
    description: 'Impact financier annuel des commissions intermédiaires.',
    source: 'finance.intermediary_loss_annual',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },

  // ===== OPERATIONS =====
  missions_actives: {
    key: 'missions_actives',
    label: 'Missions actives',
    description: 'Nombre total de missions en cours.',
    source: 'operations.active_missions',
    domain: KpiDomain.OPERATIONS,
    format: KpiFormat.NUMBER,
    unit: KpiUnit.MISSIONS,
  },

  missions_freelance: {
    key: 'missions_freelance',
    label: 'Freelance',
    description: 'Nombre de missions portées par des freelances.',
    source: 'operations.missions_freelance',
    domain: KpiDomain.OPERATIONS,
    format: KpiFormat.NUMBER,
    unit: KpiUnit.MISSIONS,
  },

  missions_cdi: {
    key: 'missions_cdi',
    label: 'CDI',
    description: 'Nombre de missions portées par des CDI.',
    source: 'operations.missions_cdi',
    domain: KpiDomain.OPERATIONS,
    format: KpiFormat.NUMBER,
    unit: KpiUnit.MISSIONS,
  },

  missions_via_intermediaire: {
    key: 'missions_via_intermediaire',
    label: 'Via intermédiaire',
    description: 'Nombre de missions opérées via intermédiaire.',
    source: 'operations.missions_via_intermediaire',
    domain: KpiDomain.OPERATIONS,
    format: KpiFormat.NUMBER,
    unit: KpiUnit.MISSIONS,
  },

  // ===== OBJECTIFS =====
  tjm_moyen_cible: {
    key: 'tjm_moyen_cible',
    label: 'TJM moyen cible',
    description: 'TJM moyen objectif défini pour l\'exercice.',
    source: 'objectives.tjm_target',
    domain: KpiDomain.FINANCE,
    format: KpiFormat.MONEY,
    unit: KpiUnit.EUROS,
  },
};

/**
 * Helper - récupère le label d'une KPI
 * @param key Clé du KPI
 * @returns Label ou clé si non trouvée
 */
export function getAdminKpiLabel(key: string): string {
  return ADMIN_KPI_REGISTRY[key]?.label ?? key;
}

/**
 * Helper - récupère la description d'une KPI (destinée à l'UI)
 * @param key Clé du KPI
 * @returns Description ou chaîne vide si non trouvée
 */
export function getAdminKpiDescription(key: string): string {
  return ADMIN_KPI_REGISTRY[key]?.description ?? '';
}

/**
 * Helper - récupère l'objet KPI complet
 * @param key Clé du KPI
 * @returns Objet KPI complet ou null
 */
export function getAdminKpi(key: string): AdminKpi | null {
  return ADMIN_KPI_REGISTRY[key] ?? null;
}

/**
 * Helper - vérifie si une clé KPI existe
 * @param key Clé du KPI
 * @returns true si la clé existe
 */
export function hasAdminKpi(key: string): boolean {
  return key in ADMIN_KPI_REGISTRY;
}

/**
 * Helper - récupère tous les KPI d'un domaine
 * @param domain Domaine fonctionnel
 * @returns Tableau des KPI du domaine
 */
export function getAdminKpisByDomain(domain: KpiDomain): AdminKpi[] {
  return Object.values(ADMIN_KPI_REGISTRY).filter((kpi) => kpi.domain === domain);
}
